import { useState } from "react";
import { motion } from "framer-motion";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Quote } from "lucide-react";

const DEVOTEES = [
  {
    id: "steve-jobs",
    name: "Steve Jobs",
    role: "Co-founder of Apple",
    year: "1974",
    short: "Traveled to India seeking Maharaj-ji — a pilgrimage that shaped the intuition behind Apple.",
    full: "In 1974, a barefoot 19-year-old Steve Jobs traveled across India hoping to meet Neem Karoli Baba at Kainchi Dham. Baba had taken mahasamadhi just months before, but Jobs stayed at the ashram and absorbed the atmosphere. He later said the trip taught him the power of intuition over intellect — a lesson that shaped every Apple product. Jobs kept Baba's teachings close all his life, and years later gave one piece of advice to another young founder: visit Kainchi Dham.",
    quote: "The people in the Indian countryside don't use their intellect like we do — they use their intuition… intuition is a very powerful thing.",
  },
  {
    id: "mark-zuckerberg",
    name: "Mark Zuckerberg",
    role: "Founder of Meta (Facebook)",
    year: "Early Facebook years",
    short: "On Steve Jobs' advice, he made his own pilgrimage to Baba's Kainchi Dham ashram.",
    full: "When Facebook was going through a difficult early phase and an offer to sell the company was on the table, Zuckerberg sought advice from Steve Jobs. Jobs told him to visit a temple in India — the ashram of Neem Karoli Baba at Kainchi Dham. Zuckerberg spent nearly a month there in reflection, and returned with clarity of mission. He shared the story publicly in 2015 during a town hall with India's Prime Minister Narendra Modi.",
    quote: "He told me that in order to reconnect with what I believed as the mission of the company, I should visit this temple that he had gone to in India.",
  },
  {
    id: "julia-roberts",
    name: "Julia Roberts",
    role: "Academy Award–winning actress",
    year: "2000s",
    short: "Found her spiritual path through a photograph of Maharaj-ji.",
    full: "Julia Roberts has spoken openly about being drawn to Neem Karoli Baba simply by seeing his photograph — an instant, inexplicable recognition. She became a devoted practitioner of Hinduism, naming her devotion in interviews and visiting temples with her family. She has said she was so drawn to Baba that she felt she had known him all her life.",
    quote: "I'm so taken with this picture of this person, Neem Karoli Baba — I was so drawn to him that I feel like I've known him my whole life.",
  },
  {
    id: "ram-dass",
    name: "Ram Dass",
    role: "Author of 'Be Here Now'",
    year: "1967",
    short: "The Harvard professor whose meeting with Baba introduced millions in the West to Maharaj-ji.",
    full: "Dr. Richard Alpert — fired from Harvard for his psychedelic research — met Baba in 1967 in the Himalayas. Baba knew intimate details of Alpert's life no one could have known, including private thoughts about his mother's death. When Baba demanded his 'yogi medicine' and swallowed an enormous dose of LSD with no effect whatsoever, Alpert's worldview shattered. Baba renamed him Ram Dass — 'servant of God.' His 1971 book 'Be Here Now' became a spiritual landmark and carried Baba's love to the entire Western world.",
    quote: "I felt that I had come home — that this was somebody who knew what I was looking for.",
  },
  {
    id: "krishna-das",
    name: "Krishna Das",
    role: "Kirtan singer",
    year: "1970",
    short: "Met Baba in 1970; became the voice that carries Maharaj-ji's name through sacred chant worldwide.",
    full: "Jeffrey Kagel met Maharaj-ji in 1970 and was renamed Krishna Das. After Baba's mahasamadhi, grief opened his heart to chanting — and he discovered that singing the names of God was the way back to his guru's love. Today he is the most beloved kirtan singer in the West, filling concert halls with the Hanuman Chalisa and the name of Ram. He says simply: 'Baba is the driver of the bus.'",
    quote: "Every time I sing, I am singing to Maharaj-ji. He's the one who opens the heart.",
  },
  {
    id: "larry-brilliant",
    name: "Dr. Larry Brilliant",
    role: "Physician · Smallpox eradication leader",
    year: "1970s",
    short: "Baba sent him to Delhi with a prophecy: you will help eradicate smallpox. He did.",
    full: "A young American doctor living in a Himalayan cave was told by Baba: 'Go to Delhi. You will work for the United Nations. You will help eradicate smallpox — for smallpox will be eradicated. This is God's gift to mankind.' Brilliant followed the instruction, joined the WHO smallpox program in India, and became one of the leaders of the campaign that eradicated smallpox from the Earth in 1980. He later led Google.org and won the TED Prize — always keeping Baba's photograph on his desk.",
    quote: "Maharaj-ji said smallpox would be eradicated because it was God's gift to humanity that it be so. And it was.",
  },
];

export default function FamousDevotees() {
  const [active, setActive] = useState(null);

  return (
    <section id="devotees" data-testid="devotees-section" className="py-20 sm:py-28 bg-[#FDFBF7]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl">
          <p className="eyebrow mb-4">05 · Touching Every Shore</p>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-amber-950" data-testid="devotees-heading">
            The Seekers Who Found Baba
          </h2>
          <p className="mt-5 text-stone-600 leading-relaxed">
            From Silicon Valley founders to Hollywood, from Harvard to the World Health Organization —
            Maharaj-ji's love reached the unlikeliest hearts. Their stories lead back to the same
            blanket-wrapped saint. Tap any story to read it in full.
          </p>
        </div>

        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {DEVOTEES.map((d, i) => (
            <motion.button key={d.id}
              type="button"
              onClick={() => setActive(d)}
              initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ duration: 0.55, delay: (i % 3) * 0.1 }}
              data-testid={`devotee-card-${d.id}`}
              className="text-left group bg-white rounded-2xl border border-amber-900/10 p-7 hover:border-amber-600/40 hover:shadow-xl hover:shadow-amber-900/10 hover:-translate-y-1.5 transition-all duration-300 cursor-pointer"
            >
              <p className="text-xs uppercase tracking-[0.2em] text-amber-700 font-semibold">{d.year}</p>
              <h3 className="font-display text-xl text-amber-950 mt-2">{d.name}</h3>
              <p className="text-sm text-stone-500 font-medium mt-0.5">{d.role}</p>
              <p className="mt-3.5 text-sm leading-relaxed text-stone-600">{d.short}</p>
              <span className="mt-4 inline-block text-xs font-semibold text-orange-700 group-hover:translate-x-1 transition-transform duration-300">
                Read their story →
              </span>
            </motion.button>
          ))}
        </div>
      </div>

      <Dialog open={!!active} onOpenChange={() => setActive(null)}>
        <DialogContent data-testid="devotee-story-dialog" className="max-w-lg bg-[#FDFBF7] border-amber-900/20 max-h-[85vh] overflow-y-auto">
          {active && (
            <>
              <DialogHeader>
                <p className="text-xs uppercase tracking-[0.2em] text-amber-700 font-semibold">{active.year}</p>
                <DialogTitle className="font-display text-2xl text-amber-950">{active.name}</DialogTitle>
                <DialogDescription className="text-stone-500">{active.role}</DialogDescription>
              </DialogHeader>
              <div className="mt-2 rounded-xl bg-amber-100/60 border border-amber-900/10 p-5">
                <Quote className="h-5 w-5 text-amber-700 mb-2" />
                <p className="font-quote italic text-lg text-amber-950 leading-snug">{active.quote}</p>
              </div>
              <p className="mt-4 text-sm leading-relaxed text-stone-700">{active.full}</p>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
